import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { statusColors } from '@/styles/theme';
import { chartTooltipProps } from '@/lib/chartTheme';
import type { StatusData } from '@/hooks';

interface WorkOrderStatusChartProps {
  data?: StatusData[];
}

export function WorkOrderStatusChart({ data = [] }: WorkOrderStatusChartProps) {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();

  const total = data.reduce((sum, item) => sum + item.value, 0);

  // Localize each slice's label from its status key (Recharts uses `name` for
  // the legend and tooltip). Falls back to the raw status if no key exists.
  const localizedData = data.map((item) => ({
    ...item,
    name: t(`statusLabels.${item.status}`, { defaultValue: item.name }),
  }));

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('workOrderStatus')}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t('totalOrders', { count: total })}
        </Typography>

        <Box sx={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={localizedData}
                cx="50%"
                cy="45%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
              >
                {localizedData.map((entry) => (
                  <Cell
                    key={entry.status}
                    fill={statusColors[entry.status]}
                    stroke={theme.palette.background.paper}
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip
                {...chartTooltipProps(theme)}
                formatter={(value: number, name: string) => [
                  `${value} ${t('orders')}`,
                  name,
                ]}
              />
              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
                iconType="circle"
                iconSize={10}
                formatter={(value) => (
                  <span style={{ color: theme.palette.text.primary, fontSize: 12 }}>
                    {value}
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}
